import { useCallback, useMemo } from 'react';
import { Image, Link, Text, VStack } from '@direct-frontend/components';
import { getSettingLoyaltyProgramUrl } from '@/entities/merchSetting';
import type { OrderLoyalty } from '@/shared/api/order';
import { yandexLogo } from '@/shared/assets/yandexLogo';
import { BonusBadge } from '@/shared/components/BonusBadge';
import { Drawer, DrawerButton, DrawerHeader, type DrawerProps } from '@/shared/components/Drawer';
import { passportAuthenticate } from '@/shared/lib/passportAuthenticate';
import { getRequestContext } from '@/shared/lib/requestContext';
import { sessionStorageHelper, SessionStorageKey } from '@/shared/lib/sessionStorage';
import styles from './LoyaltyPromoModal.module.css';

export interface LoyaltyPromoModalUIProps extends Pick<DrawerProps, 'isOpen' | 'onClose'> {
    loyaltyProgramUrl: string;
    onAuthenticate: VoidFunction;
    bonuses: number;
}

export const LoyaltyPromoModalUI = (props: LoyaltyPromoModalUIProps) => {
    const { isOpen, loyaltyProgramUrl, bonuses, onClose, onAuthenticate } = props;

    const logoIcon = useMemo(() => <Image width="24" height="24" src={yandexLogo} alt="Яндекс" />, []);

    const hasBonuses = bonuses > 0;

    return (
        <Drawer isOpen={isOpen} onClose={onClose} snapPoints={[1]} size="auto">
            <VStack className={styles.content}>
                <VStack gap="16">
                    <DrawerHeader title="Программа лояльности" />

                    <Text size="mc">
                        Подключитесь к{' '}
                        <Link href={loyaltyProgramUrl} target="_blank">
                            программе лояльности
                        </Link>
                        , чтобы получать бонусы за покупки и тратить их на заказы
                    </Text>
                </VStack>

                {hasBonuses && <BonusBadge description="За покупку товаров в корзине">+{bonuses}</BonusBadge>}
            </VStack>

            <VStack className={styles.button}>
                <DrawerButton addonStart={logoIcon} onClick={onAuthenticate}>
                    Войти c Яндекс ID
                </DrawerButton>
            </VStack>
        </Drawer>
    );
};

interface LoyaltyPromoModalProps extends Pick<DrawerProps, 'isOpen' | 'onClose'> {
    loyalty?: OrderLoyalty;
}

export const LoyaltyPromoModal = (props: LoyaltyPromoModalProps) => {
    const { loyalty, ...drawerProps } = props;
    const {
        yandexPassportEnv,
        company: { companyShopUrl },
    } = getRequestContext();

    const loyaltyProgramUrl = getSettingLoyaltyProgramUrl(companyShopUrl);

    const onAuthenticate = useCallback(() => {
        sessionStorageHelper.setItem(SessionStorageKey.LOYALTY_AUTH_PENDING, '1');
        passportAuthenticate(yandexPassportEnv);
    }, [yandexPassportEnv]);

    if (!loyalty || loyalty.is_registered_user) {
        return null;
    }

    return (
        <LoyaltyPromoModalUI
            {...drawerProps}
            bonuses={loyalty.unregistered_user_loyalty.potential_bonuses}
            loyaltyProgramUrl={loyaltyProgramUrl}
            onAuthenticate={onAuthenticate}
        />
    );
};
